import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useContactDrawer } from "../context/ContactDrawerContext";
import { opgaverData } from "../data/siteData";

export default function Header() {
  const { openDrawer } = useContactDrawer();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  const [services, setServices] = useState([]);
  const [isMobileOpgaverOpen, setIsMobileOpgaverOpen] = useState(false);
  const location = useLocation();

  // Fetch CMS services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/services");
        if (res.ok) {
          const data = await res.json();
          setServices(data);
        }
      } catch (err) {
        console.error("Kunne ikke hente ydelser til menu:", err);
      }
    };
    fetchServices();
  }, []);

  // Track scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track window width for burgermenu breakpoint (1200px)
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const isMobile = windowWidth < 1200;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-brand-sand-200/50 shadow-sm">
      {/* Top Banner */}
      <div
        className={`hidden sm:block bg-white text-neutral-700 text-sm transition-all duration-300 overflow-hidden border-b border-brand-sand-200 ${
          isScrolled ? "max-h-0 py-0 opacity-0 border-none" : "max-h-12 py-2 px-6"
        }`}
      >
        <div className="max-w-7xl mx-auto flex flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-6">
            <a href="tel:+4520661517" className="hover:text-black transition-colors flex items-center gap-1.5 font-medium">
              <i className="fa-light fa-phone text-black" aria-hidden="true" />
              <span>+45 20 66 15 17</span>
            </a>
            <a href="mailto:rasmus@elmegaardenhaveoganlaeg.dk" className="hover:text-black transition-colors flex items-center gap-1.5 font-medium">
              <i className="fa-light fa-envelope text-black" aria-hidden="true" />
              <span>rasmus@elmegaardenhaveoganlaeg.dk</span>
            </a>
          </div>
          <div className="text-xs text-neutral-500 font-medium">
            Hobro • Arden • Hadsund • Hele Nordjylland
          </div>
        </div>
      </div>

      {/* Top Bar with Logo, Navigation & CTA */}
      <div
        className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-300 ${
          isScrolled ? "py-2" : "py-4"
        }`}
      >
        {/* Logo */}
        <Link to="/" className="group flex items-center gap-3 shrink-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/238887662-105023521895230-2092375161697519941-n.png"
            alt="Elmegaarden Logo"
            className="h-14 md:h-18 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {!isMobile && (
          <nav className="flex items-center gap-8 py-1">
            <Link
              to="/"
              className={`text-sm font-bold tracking-wider transition-colors duration-300 ${
                isActive("/") && location.pathname === "/"
                  ? "text-black"
                  : "text-brand-charcoal hover:text-[#7ca632]"
              }`}
            >
              Hjem
            </Link>

            <Link
              to="/om-os"
              className={`text-sm font-bold tracking-wider transition-colors duration-300 ${
                isActive("/om-os")
                  ? "text-black"
                  : "text-brand-charcoal hover:text-[#7ca632]"
              }`}
            >
              Om os
            </Link>

            {/* Dropdown for Opgaver */}
            <div className="relative group py-3">
              <Link
                to="/opgaver"
                className={`text-sm font-bold tracking-wider transition-colors duration-300 flex items-center gap-1.5 cursor-pointer focus:outline-none ${
                  isActive("/opgaver") || isActive("/andre-opgaver") || isActive("/vi-tilbyder")
                    ? "text-black"
                    : "text-brand-charcoal hover:text-[#7ca632]"
                }`}
              >
                Opgaver{" "}
                <i className="fa-solid fa-chevron-down text-[10px] transition-transform duration-300 group-hover:rotate-180" />
              </Link>

              <div className="absolute left-0 top-full pt-2 w-72 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300 z-50">
                <div className="bg-black border border-neutral-800 rounded-sm shadow-xl py-2 text-left">
                  {opgaverData.map((opg) => (
                    <Link
                      key={opg.slug}
                      to={`/opgaver/${opg.slug}`}
                      className="block px-5 py-2.5 text-sm font-semibold text-neutral-200 hover:text-white hover:bg-neutral-900 transition-colors"
                    >
                      {opg.menuTitle}
                    </Link>
                  ))}
                  <Link
                    to="/andre-opgaver"
                    className="block px-5 py-2.5 text-sm font-semibold text-neutral-200 hover:text-white hover:bg-neutral-900 transition-colors"
                  >
                    Andre opgaver
                  </Link>
                  {services
                    .filter((s) => s.slug?.da === "autoriseret-kloakarbejde" || s.slug === "autoriseret-kloakarbejde")
                    .map((srv) => (
                      <Link
                        key={srv.id}
                        to={`/vi-tilbyder/${srv.slug?.da || srv.slug}`}
                        className="block px-5 py-2.5 text-sm font-semibold text-[#7ca632] hover:text-white hover:bg-neutral-900 transition-colors border-t border-neutral-800/80"
                      >
                        {srv.title?.da || srv.title}
                      </Link>
                    ))}
                </div>
              </div>
            </div>

            <Link
              to="/galleri"
              className={`text-sm font-bold tracking-wider transition-colors duration-300 ${
                isActive("/galleri") || isActive("/projekter")
                  ? "text-black"
                  : "text-brand-charcoal hover:text-[#7ca632]"
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/kontakt"
              className={`text-sm font-bold tracking-wider transition-colors duration-300 ${
                isActive("/kontakt")
                  ? "text-black"
                  : "text-brand-charcoal hover:text-[#7ca632]"
              }`}
            >
              Kontakt
            </Link>
          </nav>
        )}

        {/* CTA Button & Mobile Menu Button */}
        <div className="flex items-center gap-4 shrink-0">
          {!isMobile && (
            <button
              onClick={openDrawer}
              className="px-5 py-2.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-semibold text-xs tracking-wide uppercase rounded-sm transition-all duration-300 hover:shadow-md cursor-pointer focus:outline-none"
            >
              Få et tilbud
            </button>
          )}

          {/* Mobile Menu Button (< 1200px) */}
          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-brand-charcoal hover:text-[#7ca632] p-2 transition-colors focus:outline-none cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${isMobileMenuOpen ? "fa-xmark" : "fa-bars"} text-2xl`} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navigation Sidebar (< 1200px) */}
      {isMobile && isMobileMenuOpen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-black/60 z-40 transition-opacity duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Sidebar Drawer */}
          <div className="fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-black text-white z-50 shadow-2xl flex flex-col p-6 overflow-y-auto transition-transform duration-300 text-left">
            {/* Sidebar Header */}
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-neutral-800">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/238887662-105023521895230-2092375161697519941-n.png"
                  alt="Elmegaarden Logo"
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white hover:text-[#7ca632] p-1 transition-colors focus:outline-none cursor-pointer"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-2xl" />
              </button>
            </div>

            <nav className="flex flex-col gap-5 text-base">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`pb-2 border-b border-neutral-800 tracking-wider ${
                  isActive("/") && location.pathname === "/" ? "text-[#7ca632] font-bold" : "text-white hover:text-[#7ca632]"
                }`}
              >
                Hjem
              </Link>

              <Link
                to="/om-os"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`pb-2 border-b border-neutral-800 tracking-wider ${
                  isActive("/om-os") ? "text-[#7ca632] font-bold" : "text-white hover:text-[#7ca632]"
                }`}
              >
                Om os
              </Link>

              {/* Collapsible Opgaver for mobile */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between pb-2 border-b border-neutral-800">
                  <Link
                    to="/opgaver"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`tracking-wider ${
                      isActive("/opgaver") ? "text-[#7ca632] font-bold" : "text-white hover:text-[#7ca632]"
                    }`}
                  >
                    Opgaver
                  </Link>
                  <button
                    onClick={() => setIsMobileOpgaverOpen(!isMobileOpgaverOpen)}
                    className="text-neutral-400 hover:text-white p-1"
                    aria-label="Åbn undermenu"
                  >
                    <i
                      className={`fa-solid fa-chevron-down text-xs transition-transform duration-300 ${
                        isMobileOpgaverOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {isMobileOpgaverOpen && (
                  <div className="flex flex-col gap-3 pl-4 pt-3 pb-2 border-b border-neutral-800">
                    {opgaverData.map((opg) => (
                      <Link
                        key={opg.slug}
                        to={`/opgaver/${opg.slug}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`text-sm tracking-wide ${
                          isActive(`/opgaver/${opg.slug}`)
                            ? "text-[#7ca632] font-bold"
                            : "text-neutral-300 hover:text-white"
                        }`}
                      >
                        {opg.menuTitle}
                      </Link>
                    ))}
                    <Link
                      to="/andre-opgaver"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`text-sm tracking-wide ${
                        isActive("/andre-opgaver")
                          ? "text-[#7ca632] font-bold"
                          : "text-neutral-300 hover:text-white"
                      }`}
                    >
                      Andre opgaver
                    </Link>
                    {services.map((srv) => {
                      const srvTitle = srv.title?.da || srv.title || "";
                      const srvSlug = srv.slug?.da || srv.slug || "";
                      return (
                        <Link
                          key={srv.id}
                          to={`/vi-tilbyder/${srvSlug}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`text-sm tracking-wide ${
                            isActive(`/vi-tilbyder/${srvSlug}`)
                              ? "text-[#7ca632] font-bold"
                              : "text-neutral-300 hover:text-white"
                          }`}
                        >
                          {srvTitle}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              <Link
                to="/galleri"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`pb-2 border-b border-neutral-800 tracking-wider ${
                  isActive("/galleri") || isActive("/projekter") ? "text-[#7ca632] font-bold" : "text-white hover:text-[#7ca632]"
                }`}
              >
                Galleri
              </Link>

              <Link
                to="/kontakt"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`pb-2 border-b border-neutral-800 tracking-wider text-left ${
                  isActive("/kontakt") ? "text-[#7ca632] font-bold" : "text-white hover:text-[#7ca632]"
                }`}
              >
                Kontakt
              </Link>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openDrawer();
                }}
                className="mt-6 w-full py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-center text-sm tracking-wide uppercase rounded-sm transition-colors cursor-pointer focus:outline-none"
              >
                Få et uforpligtende tilbud
              </button>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
